import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import debounce from 'lodash.debounce';
import DataTable from "./components/datatable/DataTable";
import { audienceColumns } from "./audienceColumns";
import { AuthContext } from "../../contexts";
import { AUDIENCE_LIST } from "../../endpoints";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export const usePagination = () => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10, range: [] });
  return {
    onPaginationChange: setPagination,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    pagination,
    setPagination
  };
};

const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : null);

function IndexPage() {

  const { onPaginationChange, page, limit, pagination, setPagination } = usePagination();
  const [totalCount, setTotalCount] = useState(0);
  const [tableData, setTableData] = useState({ records: [], pagination });
  const [filters, setFilters] = useState({
    searchKeyword: '',
    learningStartDate: '',
    learningEndDate: '',
    recentStartDate: '',
    recentEndDate: '',
    activityStatus: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const { token } = React.useContext(AuthContext);

  const fetchData = debounce(async () => {
    setIsLoading(true);
    try {
      const {
        searchKeyword,
        learningStartDate,
        learningEndDate,
        recentStartDate,
        recentEndDate,
        activityStatus,
      } = filters;

      const activityStatusParam = activityStatus.map((status, index) => `activityStatus[]=${encodeURIComponent(status)}`).join('&');

      const response = await axios.request({
        url: `${AUDIENCE_LIST}?page=${page}&limit=${limit}&keyword=${searchKeyword}&learningStart[startDate]=${formatDate(learningStartDate)}&learningStart[endDate]=${formatDate(learningEndDate)}&recentActivity[startDate]=${formatDate(recentStartDate)}&recentActivity[endDate]=${formatDate(recentEndDate)}&${activityStatusParam}`,
        headers: { Authorization: token },
      });

      const { data } = response;
      setTableData({
        records: data.records,
        pagination: { ...pagination, ...data.pagination, pageSize: data.pagination.limit }
      });
      setTotalCount(data.total_count);
    } catch (error) {
      toast.error("Something went wrong");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchData();
  }, [page, limit, filters]);

  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: 10 }));
  };

  return (
    <div className="audience-page">
      <ToastContainer position="bottom-right" autoClose={3500} />
      <section className="section-audience-header">
        <div className="page-padding">
          <div className="audience-header">
            <h2>Audience</h2>
            <div className="audience-tab-menu">
              <a className="link-audience-tab active" href="#">
                <span className="icon-link-audience-tab">
                <svg width="26" height="20" viewBox="0 0 26 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M21.4585 5.13137C21.4585 7.96626 19.162 10.2627 16.3271 10.2627C13.4922 10.2627 11.1957 7.96626 11.1957 5.13137C11.1957 2.29649 13.4922 0 16.3271 0C19.162 0 21.4585 2.29649 21.4585 5.13137ZM19.5925 5.13137C19.5925 3.32703 18.1314 1.86595 16.3271 1.86595C14.5227 1.86595 13.0617 3.32703 13.0617 5.13137C13.0617 6.93572 14.5227 8.39679 16.3271 8.39679C18.1314 8.39679 19.5925 6.93572 19.5925 5.13137Z" fill="#307E93"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.9303 16.4604C7.9303 12.8402 11.9411 11.1957 16.5603 11.1957C21.1796 11.1957 25.1904 12.8402 25.1904 16.4604C25.1904 18.3417 24.4616 19.126 22.8991 19.126H10.2216C8.65906 19.126 7.9303 18.3417 7.9303 16.4604ZM9.84855 17.2264C9.78844 17.1749 9.79005 17.0513 9.79385 16.7604C9.79496 16.6755 9.79626 16.5762 9.79626 16.4604C9.79626 14.3256 12.8788 13.0617 16.5603 13.0617C20.2418 13.0617 23.3244 14.3256 23.3244 16.4604C23.3244 16.5762 23.3257 16.6754 23.3268 16.7603C23.3306 17.0512 23.3322 17.1749 23.2721 17.2264C23.2278 17.2644 23.1498 17.2632 23.0144 17.2612C22.9798 17.2607 22.9415 17.2601 22.8991 17.2601H10.2216C10.1792 17.2601 10.1409 17.2607 10.1064 17.2612C9.9709 17.2632 9.8929 17.2644 9.84855 17.2264Z" fill="#307E93"/>
                  <path d="M9.05651 6.33761C8.69216 5.97326 8.10143 5.97326 7.73708 6.33761L3.4517 10.6226L1.59269 8.76335L1.5048 8.68574C1.1388 8.40116 0.609586 8.42703 0.273263 8.76335C-0.0910875 9.1277 -0.0910875 9.71843 0.273263 10.0828L2.7923 12.6018L2.88019 12.6794C3.24619 12.964 3.77541 12.9381 4.11173 12.6018L9.05651 7.65704L9.13412 7.56915C9.4187 7.20315 9.39283 6.67394 9.05651 6.33761Z" fill="#307E93"/>
                </svg>
                </span>
                <span className="text-link-audience-tab">Learners</span>
                {totalCount ? <span className="counter-link-audience-tab">{totalCount}</span> : null}
              </a>
              <a className="link-audience-tab pro-link-tab disabled" href="#">
                <span className="icon-link-audience-tab">
                  <svg width="29" height="20" viewBox="0 0 29 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.0633 10.6881C18.4083 10.6881 21.3761 12.6667 21.3761 15.7508C21.3761 17.9223 19.9316 19.126 18.001 19.126H10.1255C8.19486 19.126 6.75036 17.9223 6.75036 15.7508C6.75036 12.6667 9.71823 10.6881 14.0633 10.6881ZM23.0637 10.6881C26.1529 10.6881 28.1265 12.552 28.1265 14.907C28.1265 16.8112 26.6723 18.001 25.0326 18.001H24.1888C23.5674 18.001 23.0637 17.4973 23.0637 16.8759C23.0637 16.2546 23.5674 15.7508 24.1888 15.7508H25.0326C25.518 15.7508 25.8764 15.4576 25.8764 14.907C25.8764 13.8244 24.9381 12.9382 23.0637 12.9382C22.4424 12.9382 21.9387 12.4345 21.9387 11.8131C21.9387 11.1918 22.4424 10.6881 23.0637 10.6881ZM5.06277 10.6881C5.68412 10.6881 6.18783 11.1918 6.18783 11.8131C6.18783 12.4345 5.68412 12.9382 5.06277 12.9382C3.18845 12.9382 2.25012 13.8244 2.25012 14.907C2.25012 15.4576 2.6085 15.7508 3.09392 15.7508H3.93771C4.55906 15.7508 5.06277 16.2546 5.06277 16.8759C5.06277 17.4973 4.55906 18.001 3.93771 18.001H3.09392C1.45422 18.001 0 16.8112 0 14.907C0 12.552 1.97359 10.6881 5.06277 10.6881ZM14.0633 12.9382C10.8141 12.9382 9.00048 14.1473 9.00048 15.7508C9.00048 16.5796 9.35608 16.8759 10.1255 16.8759H18.001C18.7704 16.8759 19.126 16.5796 19.126 15.7508C19.126 14.1473 17.3124 12.9382 14.0633 12.9382ZM20.8136 1.12506C22.9884 1.12506 24.7513 2.88803 24.7513 5.06277C24.7513 7.23751 22.9884 9.00048 20.8136 9.00048C20.1923 9.00048 19.6886 8.49678 19.6886 7.87542C19.6886 7.25407 20.1923 6.75036 20.8136 6.75036L20.9761 6.74264C21.8319 6.66086 22.5012 5.93998 22.5012 5.06277C22.5012 4.13074 21.7456 3.37518 20.8136 3.37518L20.6824 3.36761C20.1229 3.30262 19.6886 2.82709 19.6886 2.25012C19.6886 1.62877 20.1923 1.12506 20.8136 1.12506ZM7.31289 1.12506C7.93425 1.12506 8.43795 1.62877 8.43795 2.25012C8.43795 2.82709 8.00363 3.30262 7.4441 3.36761L7.31289 3.37518C6.38086 3.37518 5.6253 4.13074 5.6253 5.06277C5.6253 5.93998 6.29459 6.66086 7.15037 6.74264L7.31289 6.75036C7.93425 6.75036 8.43795 7.25407 8.43795 7.87542C8.43795 8.49678 7.93425 9.00048 7.31289 9.00048C5.13815 9.00048 3.37518 7.23751 3.37518 5.06277C3.37518 2.88803 5.13815 1.12506 7.31289 1.12506ZM14.0633 0C16.5487 0 18.5635 2.01483 18.5635 4.50024C18.5635 6.98566 16.5487 9.00048 14.0633 9.00048C11.5778 9.00048 9.56301 6.98566 9.56301 4.50024C9.56301 2.01483 11.5778 0 14.0633 0ZM14.0633 2.25012C12.8205 2.25012 11.8131 3.25753 11.8131 4.50024C11.8131 5.74295 12.8205 6.75036 14.0633 6.75036C15.306 6.75036 16.3134 5.74295 16.3134 4.50024C16.3134 3.25753 15.306 2.25012 14.0633 2.25012Z" fill="#6A777A"/>
                  </svg>
                </span>
                <span className="text-link-audience-tab">Groups</span>
                <span className="tag-link-audience-tab tag-link-audience-tab--pro">Pro</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <DataTable
        columns={audienceColumns}
        data={tableData}
        isLoading={isLoading}
        toolbarType="main"
        onPaginationChange={onPaginationChange}
        isPaginationEnabled
        onSearchChange={handleFilterChange('searchKeyword')}
        onLearningStartDateChange={handleFilterChange('learningStartDate')}
        onLearningEndDateChange={handleFilterChange('learningEndDate')}
        onRecentStartDateChange={handleFilterChange('recentStartDate')}
        onRecentEndDateChange={handleFilterChange('recentEndDate')}
        onActivityStatusChange={handleFilterChange('activityStatus')}
        resultLabel={tableData?.records?.length ? `Showing ${eval((page - 1) * limit + 1)}-${eval((page - 1) * limit + tableData.records.length)} of ${totalCount}` : ""}
      />
    </div>
  );
}

export default IndexPage;
